import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if Supabase is configured
    if (!isSupabaseConfigured()) {
      console.error('Supabase is not properly configured');
      setLoading(false);
      return;
    }

    // Get initial session with error handling
    supabase?.auth?.getSession()
      ?.then(({ data: { session } }) => {
        setUser(session?.user ?? null);
        setLoading(false);
      })
      ?.catch((err) => {
        console.error('Failed to get session:', err?.message);
        setLoading(false);
      });

    // Listen for auth changes (MUST be synchronous)
    let subscription;
    try {
      const { data } = supabase?.auth?.onAuthStateChange((event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      });
      subscription = data?.subscription;
    } catch (err) {
      console.error('Failed to subscribe to auth changes:', err?.message);
      setLoading(false);
    }

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = async (email, password, fullName) => {
    // Validate Supabase configuration
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication service is not configured. Please check your environment variables.');
    }

    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: 'employee'
        }
      }
    });
    
    if (error) throw error;
    
    // Return full data object including session info
    return { data, error: null };
  };

  const signIn = async (email, password) => {
    // Validate Supabase configuration
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication service is not configured. Please check your environment variables.');
    }

    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password
    });
    
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    // Validate Supabase configuration
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication service is not configured. Please check your environment variables.');
    }

    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};